import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  Settings,
  Scissors,
  Cloud,
  CloudOff,
  BarChart2,
  FileSpreadsheet
} from "lucide-react";

const navItems = [
  { to: "/", label: "Dashboard", mobileLabel: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/orders", label: "Pesanan", mobileLabel: "Pesanan", icon: ShoppingBag, testid: "nav-orders" },
  { to: "/customers", label: "Customer", mobileLabel: "Customer", icon: Users, testid: "nav-customers" },
  { to: "/reports", label: "Laporan", mobileLabel: "Laporan", icon: BarChart2, testid: "nav-reports" },
  { to: "/settings", label: "Pengaturan", mobileLabel: "Setelan", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const [drive, setDrive] = useState({ connected: false, configured: false, email: null });
  const [settings, setSettings] = useState({ brandName: "CLF", slogan: "Tailor Order", logoUrl: null });
  const location = useLocation();

  const fetchLayoutData = async () => {
    try {
      const [{ data: driveData }, { data: settingsData }] = await Promise.all([
        api.get("/drive/status").catch(() => ({ data: { connected: false, configured: false, email: null } })),
        api.get("/settings").catch(() => ({ data: {} }))
      ]);
      setDrive(driveData);
      if (settingsData) {
        setSettings(prev => ({ ...prev, ...settingsData }));
      }
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    fetchLayoutData();
    window.addEventListener('settings-updated', fetchLayoutData);
    return () => {
      window.removeEventListener('settings-updated', fetchLayoutData);
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen w-full max-w-[100vw] overflow-x-hidden flex flex-col lg:flex-row pb-16 lg:pb-0 bg-[#F6F1E8] lg:bg-transparent">
      {/* Top bar for mobile/tablet portrait */}
      <header className="flex lg:hidden items-center justify-between px-4 py-3 bg-[#16202B] text-white sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 border border-[#B89968]/40 bg-[#B89968]/10 flex items-center justify-center overflow-hidden">
            {settings.logoUrl ? (
              <img src={settings.logoUrl} alt="Logo" className="w-full h-full object-contain" />
            ) : (
              <Scissors className="h-4 w-4 text-[#D9C39A]" strokeWidth={1.5} />
            )}
          </div>
          <div>
            <div className="font-semibold text-base tracking-tight leading-none text-white">{settings.brandName || "CLF"}</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#D9C39A] mt-1">
              {settings.slogan || "Tailor Order"}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar for large screens */}
      <aside
        className="hidden lg:flex w-64 shrink-0 border-r border-[#E5DCC7] bg-white flex-col sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div
          className="px-6 py-7 border-b border-[#E5DCC7] relative shrink-0"
          style={{
            backgroundImage:
              "linear-gradient(135deg, #16202B 0%, #243345 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 border border-[#B89968]/40 bg-[#B89968]/10 flex items-center justify-center overflow-hidden">
              {settings.logoUrl ? (
                <img src={settings.logoUrl} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <Scissors className="h-4 w-4 text-[#D9C39A]" strokeWidth={1.5} />
              )}
            </div>
            <div>
              <div className="font-semibold text-lg tracking-tight leading-none text-white">{settings.brandName || "CLF"}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D9C39A] mt-1.5">
                {settings.slogan || "Tailor Order"}
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-[#B89968] via-[#B89968]/40 to-transparent" />
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {navItems.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-all relative
                ${isActive
                  ? "text-[#16202B] bg-[#F6F1E8] font-semibold border-l-2 border-[#B89968] -ml-[1px] pl-[14px]"
                  : "text-stone-600 hover:text-[#16202B] hover:bg-stone-50"
                }`
              }
            >
              <Icon className="h-4 w-4" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div
          className="px-4 py-4 border-t border-[#E5DCC7] bg-[#FAF6EC] shrink-0"
          data-testid="drive-status-indicator"
        >
          <div className="flex items-start gap-2">
            {drive.connected ? (
              <Cloud className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={1.5} />
            ) : (
              <CloudOff className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" strokeWidth={1.5} />
            )}
            <div className="min-w-0">
              <div className="text-xs text-stone-700 font-medium">
                Google Drive:{" "}
                <span className={drive.connected ? "text-emerald-700" : "text-stone-500"}>
                  {drive.connected ? "Terhubung" : "Terputus"}
                </span>
              </div>
              {drive.email && (
                <div className="text-[10px] text-stone-500 truncate mt-0.5">
                  {drive.email}
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>

      {/* Bottom nav for mobile/tablet portrait */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5DCC7] flex items-center justify-around lg:hidden z-50 pb-[env(safe-area-inset-bottom)]">
        {navItems.map(({ to, mobileLabel, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 p-2 w-full transition-colors
              ${isActive
                ? "text-[#B89968]"
                : "text-stone-500 hover:text-stone-900"
              }`
            }
          >
            <Icon className="h-5 w-5" strokeWidth={1.5} />
            <span className="text-[9px] text-center leading-tight truncate w-full">{mobileLabel}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
